import { Link } from "react-router-dom";
import SectionTitle from "@/components/SectionTitle";
import usePageTitle from "@/hooks/usePageTitle";

const initiatives = [
  { title: "Ghar Ghar Chess – Har Ghar Chess", body: "A national mass-participation programme bringing chess into every Indian home, school and community centre." },
  { title: "AICF Chess Development Fund", body: "A consolidated funding mechanism for tournaments, coaching, equipment and travel — built around an INR 65 crore commitment." },
  { title: "AICF Pro · AICF Popular", body: "Two-tier player ecosystem: an elite competitive pathway for rated players, and a mass-participation circuit for new entrants." },
  { title: "National Chess Arena", body: "AICF's national online play and rating platform — designed for one million registered Indian players." },
  { title: "State & District Support", body: "Structured grants and tournament calendars to strengthen state associations and the district chess pipeline." },
  { title: "Player Support & Coaching", body: "Monthly contracts for top juniors (₹20,000–₹50,000) and annual retainers of ₹25 lakhs for top FIDE-rated Indians." },
];

const pillars = [
  { icon: "fa-solid fa-graduation-cap", title: "Chess in Education", body: "Embed chess in school curricula and after-school clubs nationwide, in partnership with state governments." },
  { icon: "fa-solid fa-seedling", title: "Grassroots Development", body: "Strengthen the district-to-state-to-national pipeline so champions can emerge from any corner of India." },
  { icon: "fa-solid fa-user-graduate", title: "Youth Chess", body: "Identify and support U-7 to U-19 talent with structured contracts, coaching access and competitive exposure." },
  { icon: "fa-solid fa-handshake-angle", title: "State Association Support", body: "Predictable grants, technical assistance and a transparent calendar for every state and district body." },
];

export default function ChessLeadership() {
  usePageTitle("Chess Leadership", "The AICF blueprint — 'Checkmating Challenges for Chess Development in Bharat' — under President Nitin Narang. ₹65 cr development budget, India-first rating system, player welfare and India as a global host.");
  return (
    <main>
      {/* Hero */}
      <section className="section-dark relative overflow-hidden">
        <div className="absolute inset-0 chess-pattern opacity-[0.06] pointer-events-none"></div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow mb-6 flex items-center gap-3">
                <span className="w-8 h-px bg-[#b8975d]"></span>
                <span>Chess Leadership</span>
              </div>
              <h1 className="serif-title text-[clamp(2.5rem,6vw,5rem)] text-[#faf8f3] mb-6">
                Vision for Indian Chess.
              </h1>
              <p className="text-[#cfc6b3] text-[18px] leading-relaxed max-w-3xl">
                As President of the All India Chess Federation, Nitin Narang leads the national governing body for the 2024–2027 term — building a chess ecosystem anchored in player welfare, an India-first rating system, and a hosting calendar that brings the world to India.
              </p>
            </div>
            <div className="lg:col-span-4 grid grid-cols-2 gap-4">
              <div className="border border-[rgba(255,255,255,0.18)] p-5">
                <div className="font-display text-4xl text-[#b8975d]">2024</div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#cfc6b3] mt-1">Term begins</div>
              </div>
              <div className="border border-[rgba(255,255,255,0.18)] p-5">
                <div className="font-display text-4xl text-[#b8975d]">100+</div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#cfc6b3] mt-1">Indian GMs</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <SectionTitle eyebrow="The pillars" title="A four-pillar plan for Bharatiya chess." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((p, i) => (
            <div key={i} className="border hairline bg-white p-7 card-hover">
              <div className="w-12 h-12 border hairline flex items-center justify-center mb-6 text-[#b8975d]">
                <i className={p.icon}></i>
              </div>
              <h3 className="font-display text-xl text-[#0a1628] mb-3">{p.title}</h3>
              <p className="text-[13px] text-[#4a4a4a] leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Initiatives */}
      <section className="bg-white border-y hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
          <SectionTitle eyebrow="Key initiatives" title="What the AICF is building." kicker="Programmes and platforms that translate the blueprint into measurable outcomes for players, parents and partners."/>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            {initiatives.map((it, i) => (
              <div key={i} className="border-l hairline pl-6">
                <div className="eyebrow mb-2">Initiative · {String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-2xl text-[#0a1628] mb-2">{it.title}</h3>
                <p className="text-[#4a4a4a] text-[15px] leading-relaxed">{it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote block */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <figure className="max-w-4xl mx-auto text-center relative">
          <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-[120px] leading-none font-display text-[#b8975d] opacity-30">“</span>
          <blockquote className="serif-title text-3xl lg:text-4xl text-[#0a1628] leading-[1.3] italic">
            Every move, every mistake, every comeback — all of it becomes the epic story of how champions are built.
          </blockquote>
          <figcaption className="mt-8 eyebrow text-[#6b6b6b]">— Nitin Narang, FIDE World Cup 2025 closing ceremony · Goa</figcaption>
        </figure>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <div className="border hairline bg-white p-10 lg:p-14 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow mb-3">Newsroom & Videos</div>
            <h3 className="serif-title text-3xl lg:text-4xl text-[#0a1628]">Follow the work — in coverage and conversation.</h3>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link to="/news" className="btn justify-center">Read News</Link>
            <Link to="/videos" className="btn btn-ghost justify-center">Watch Videos</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
